import React, { useEffect, useState } from 'react'

import Button from '@react/react-spectrum/Button'
import Checkbox from '@react/react-spectrum/Checkbox'
import Dialog from '@react/react-spectrum/Dialog'
import FieldLabel from '@react/react-spectrum/FieldLabel'
import ModalTrigger from '@react/react-spectrum/ModalTrigger'
import Select from '@react/react-spectrum/Select'
import Wait from '@react/react-spectrum/Wait'
import { error as errorToast, success, warning as warningToast } from '@react/react-spectrum/Toast'

import A from 'services/analytics'
import { dsnApiV2 } from 'services/axios'

const TRANSLATION_LANGUAGES = [
  { label: 'Czech', language: 'cs', locale: 'cs-CZ' },
  { label: 'Danish', language: 'da', locale: 'da-DK' },
  { label: 'German', language: 'de', locale: 'de-DE' },
  { label: 'Estonian', language: 'et', locale: 'et-EE' },
  { label: 'English (United Kingdom)', language: 'en', locale: 'en-GB' },
  { label: 'English (United States)', language: 'en', locale: 'en-US' },
  { label: 'Spanish (Spain)', language: 'es', locale: 'es-ES' },
  { label: 'Spanish (Latin America)', language: 'es', locale: 'es-419' },
  { label: 'Spanish (Mexico)', language: 'es', locale: 'es-MX' },
  { label: 'French (Canada)', language: 'fr', locale: 'fr-CA' },
  { label: 'French (France)', language: 'fr', locale: 'fr-FR' },
  { label: 'Croatian', language: 'hr', locale: 'hr-HR' },
  { label: 'Italian', language: 'it', locale: 'it-IT' },
  { label: 'Latvian', language: 'lv', locale: 'lv-LV' },
  { label: 'Lithuanian', language: 'lt', locale: 'lt-LT' },
  { label: 'Hungarian', language: 'hu', locale: 'hu-HU' },
  { label: 'Dutch', language: 'nl', locale: 'nl-NL' },
  { label: 'Norwegian', language: 'no', locale: 'nb-NO' },
  { label: 'Polish', language: 'pl', locale: 'pl-PL' },
  { label: 'Portuguese (Brazil)', language: 'pt-BR', locale: 'pt-BR' },
  { label: 'Portuguese (Portugal)', language: 'pt-PT', locale: 'pt-PT' },
  { label: 'Romanian', language: 'ro', locale: 'ro-RO' },
  { label: 'Slovak', language: 'sk', locale: 'sk-SK' },
  { label: 'Slovenian', language: 'sl', locale: 'sl-SI' },
  { label: 'Finnish', language: 'fi', locale: 'fi-FI' },
  { label: 'Swedish', language: 'sv', locale: 'sv-SE' },
  { label: 'Turkish', language: 'tr', locale: 'tr-TR' },
  { label: 'Bulgarian', language: 'bg', locale: 'bg-BG' },
  { label: 'Russian', language: 'ru', locale: 'ru-RU' },
  { label: 'Serbian', language: 'sr', locale: 'sr-RS' },
  { label: 'Ukrainian', language: 'uk', locale: 'uk-UA' },
  { label: 'Chinese (Simplified)', language: 'zh-CN', locale: 'zh-CN' },
  { label: 'Chinese (Traditional)', language: 'zh-TW', locale: 'zh-TW' },
  { label: 'Japanese', language: 'ja', locale: 'ja-JP' },
  { label: 'Korean', language: 'ko', locale: 'ko-KR' },
  { label: 'Hindi', language: 'hi', locale: 'hi-IN' },
  { label: 'Bengali', language: 'bn', locale: 'bn-IN' },
  { label: 'Arabic', language: 'ar', locale: 'ar-SA' },
  { label: 'Hebrew', language: 'he', locale: 'he-IL' },
]

const SELECT_OPTIONS = TRANSLATION_LANGUAGES.map((l) => ({ label: l.label, value: l.locale }))

const LANG_BY_LOCALE = Object.fromEntries(TRANSLATION_LANGUAGES.map((l) => [l.locale, l]))

const LANG_NAMES = {
  cs: 'Czech',
  da: 'Danish',
  de: 'German',
  et: 'Estonian',
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  hr: 'Croatian',
  it: 'Italian',
  lv: 'Latvian',
  lt: 'Lithuanian',
  hu: 'Hungarian',
  nl: 'Dutch',
  no: 'Norwegian',
  pl: 'Polish',
  pt: 'Portuguese',
  'pt-PT': 'Portuguese (Portugal)',
  'pt-BR': 'Portuguese (Brazil)',
  ro: 'Romanian',
  sk: 'Slovak',
  sl: 'Slovenian',
  fi: 'Finnish',
  sv: 'Swedish',
  tr: 'Turkish',
  bg: 'Bulgarian',
  ru: 'Russian',
  sr: 'Serbian',
  uk: 'Ukrainian',
  'zh-CN': 'Chinese (Simplified)',
  'zh-TW': 'Chinese (Traditional)',
  ja: 'Japanese',
  ko: 'Korean',
  hi: 'Hindi',
  bn: 'Bengali',
  ar: 'Arabic',
  he: 'Hebrew',
}

export default function TranslateDialog({ projectId, disabled, currentLanguage }) {
  return (
    <ModalTrigger>
      <Button disabled={disabled} variant='primary'>
        Translate Project
      </Button>
      <TranslateDialogContent projectId={projectId} disabled={disabled} currentLanguage={currentLanguage} />
    </ModalTrigger>
  )
}

function TranslateDialogContent({ projectId, disabled, currentLanguage, ...otherProps }) {
  const [selectedLocale, setSelectedLocale] = useState('')
  const [updateLocale, setUpdateLocale] = useState(true)
  const [translating, setTranslating] = useState(false)

  useEffect(() => {
    A.triggerOpenTranslateDialog()
  }, [])

  const selectedLang = selectedLocale ? LANG_BY_LOCALE[selectedLocale] : null
  const canTranslate = selectedLang && !translating && !disabled

  const handleTranslate = async () => {
    if (!selectedLang) return

    setTranslating(true)
    try {
      const body = {
        projectId,
        language: selectedLang.language,
        translateProducts: true,
        translateScreenTitles: true,
      }
      if (updateLocale) {
        body.locale = selectedLang.locale
      }
      const { data } = await dsnApiV2.post('/projects/translate', body, { timeout: 60000 })
      A.triggerTranslateProject(selectedLang.language, body.locale)
      if (data?.status === 'partial') {
        // Some batches fell back to originals — project meta.language was still updated
        // because most strings really are in the new language now, but we want to surface
        // the partial outcome so the user knows to spot-check.
        warningToast(
          `Translation partially completed: ${data.translatedCount} of ${data.totalCount} ` +
            `strings translated, ${data.failedCount} left in original language. Reload to see changes.`
        )
      } else {
        success('Project translated successfully. Reload to see changes.')
      }
    } catch (err) {
      const message =
        err?.response?.data?.message || err.message || 'Translation failed. Project left in original language.'
      errorToast(message)
    } finally {
      setTranslating(false)
    }
  }

  return (
    <Dialog
      title='Translate Project'
      confirmLabel={translating ? 'Translating...' : 'Translate'}
      cancelLabel='Cancel'
      confirmDisabled={!canTranslate}
      onConfirm={handleTranslate}
      {...otherProps}
      role='dialog'
    >
      <div style={{ width: 420, minHeight: 240 }}>
        <p style={{ marginTop: 0, color: '#6e6e6e', fontSize: 13, lineHeight: 1.5 }}>
          This will translate <strong>all visible text content</strong> in the project to the selected language,
          including product names and descriptions. This operation overwrites the current content and the only way to
          undo it is by translating back to the original language.
        </p>

        <p style={{ margin: '0 0 12px', fontSize: 13 }}>
          <strong>Current language:</strong> {LANG_NAMES[currentLanguage] || currentLanguage || 'English (default)'}
        </p>

        <FieldLabel label='Target Language' labelFor='translate-language'>
          <Select
            id='translate-language'
            options={SELECT_OPTIONS}
            value={selectedLocale}
            placeholder='Select language...'
            onChange={setSelectedLocale}
            disabled={disabled || translating}
          />
        </FieldLabel>

        <div style={{ marginTop: 16 }}>
          <Checkbox
            label={selectedLang ? `Also update project locale to ${selectedLang.locale}` : 'Also update project locale'}
            checked={updateLocale}
            onChange={setUpdateLocale}
            disabled={disabled || translating}
          />
        </div>

        {translating && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
            <Wait size='S' />
            <span style={{ fontSize: 13, color: '#6e6e6e' }}>Translating project content...</span>
          </div>
        )}
      </div>
    </Dialog>
  )
}
